import classNames from 'classnames'
import React from 'react'
import ReactDOM from 'react-dom'
import { useSearchParams } from 'react-router-dom'

import { Button } from '../../../components'
import { SIDEBAR_QUERY_PARAM } from '../../sidebar/sidebar'
import { MOBILE_TOOLBAR_DIV_ID } from '../../sidebar/sidebar-tabs'
import { DrawingActions, DrawingTool, IToolBarProps } from '../interfaces'
import Toolbar from '../toolbar'
import ToolButton from './tool-button'

type ToolbarMobileProps = Omit<IToolBarProps, 'isMobile'>

export default function ToolbarMobile({
  activeTool,
  onActiveToolChange,
  onDrawingActionClick,
  ...rest
}: ToolbarMobileProps) {
  const [isToolbarVisible, setIsToolbarVisible] = React.useState(false)
  const [urlSearchParams, setUrlSearchParams] = useSearchParams()
  const [toolbarMobileElement, setToolbarMobileElement] = React.useState<HTMLElement | null>(null)

  // get toolbar mobile element on first render
  React.useEffect(() => {
    setToolbarMobileElement(document.getElementById(MOBILE_TOOLBAR_DIV_ID))
  }, [])

  const handleActiveToolChange = React.useCallback(
    (drawingTool: DrawingTool, trigger?: 'button' | 'select' | 'group') => {
      onActiveToolChange(drawingTool, trigger)

      if (trigger !== 'group') return setIsToolbarVisible(false)
    },
    [onActiveToolChange]
  )

  // Hide toolbar on click outside
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    if (!isToolbarVisible) return

    function handleClickOutside(event: TouchEvent | MouseEvent) {
      // Get the currently open select
      const openSelect = document.querySelector('.finviz-popover[data-enter]')
      if (wrapperRef.current?.contains(event.target as Node) || openSelect?.contains(event.target as Node)) return
      setIsToolbarVisible(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isToolbarVisible])

  const handleDrawingActionClick = (actionId: DrawingActions) => {
    onDrawingActionClick?.(actionId)
    setIsToolbarVisible(false)
  }

  if (!toolbarMobileElement) return null

  return ReactDOM.createPortal(
    <>
      <div
        ref={wrapperRef}
        className={classNames('absolute bottom-0', {
          'pointer-events-none invisible': !isToolbarVisible,
        })}
      >
        <Toolbar
          isMobileOverlay
          activeTool={activeTool}
          onActiveToolChange={handleActiveToolChange}
          onDrawingActionClick={handleDrawingActionClick}
          {...rest}
        />
      </div>
      {urlSearchParams.has(SIDEBAR_QUERY_PARAM) || activeTool === DrawingTool.Mouse ? (
        <Button
          leftContent="draw"
          size="medium"
          onClick={() => {
            setIsToolbarVisible(true)
            setUrlSearchParams((params) => {
              params.delete(SIDEBAR_QUERY_PARAM)
              return params
            })
          }}
        >
          Draw
        </Button>
      ) : (
        <ToolButton
          activeTool={activeTool}
          onClick={() => {
            setIsToolbarVisible(true)
          }}
        />
      )}
    </>,
    toolbarMobileElement
  )
}
